import { cn } from "@/lib/utils";
import { iniciais } from "@/lib/formato-mercado";

// ─── As peças de painel do comprador ─────────────────────────────────────────
//
// Nasceram dentro de Romaneios v2 e passaram a morar aqui quando Produtores v2
// precisou das mesmas: quatro números no topo, chips com contagem, avatar com
// iniciais. Uma cópia em cada tela divergiria na primeira mudança de espaçamento
// — e o corretor passa de uma tela para a outra com um toque.

/** Um dos quatro números do topo. `alerta` = borda âmbar; `perigo` = vermelha. */
export function Tile({
  rotulo, alerta, perigo, children, testId,
}: {
  rotulo: string;
  alerta?: boolean;
  perigo?: boolean;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <section
      className={cn(
        "rounded-lg border bg-card p-3.5 sm:p-4 flex flex-col gap-1.5 min-h-[116px] sm:min-h-[132px] min-w-0",
        alerta && "border-[hsl(var(--warning,38_92%_50%))]",
        perigo && "border-destructive/60",
      )}
      data-testid={testId}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{rotulo}</p>
      {children}
    </section>
  );
}

/** O número grande do tile, em mono, com a unidade pequena ao lado. */
export function Numerao({
  valor, unidade, testId, className,
}: { valor: string; unidade?: string; testId?: string; className?: string }) {
  return (
    // ⚠️ 16 px no celular, 26 do `sm` para cima, e SEM quebra. A 390 px o tile
    // tem ~129 px úteis (medido no Chromium): "R$ 101.343,20" são 13 caracteres
    // mono, e só cabem abaixo de ~10 px por caractere. Em 26 px quebrava em "R$"
    // numa linha e o número na outra; em 21 px, sem quebra, vazava do tile.
    <p className={cn("font-mono text-[16px] sm:text-[26px] font-semibold tabular-nums leading-tight whitespace-nowrap", className)} data-testid={testId}>
      {valor}
      {unidade && <span className="ml-1.5 text-[13px] font-medium text-muted-foreground font-sans">{unidade}</span>}
    </p>
  );
}

/**
 * Os chips de situação, com a contagem que vem PRONTA do servidor.
 *
 * Rolagem horizontal no celular: quatro ou cinco chips com contagem não cabem
 * em 390 px, e quebrá-los em duas linhas empurraria a lista para baixo da dobra
 * logo na abertura.
 */
export function ChipsDeSituacao<T extends string>({
  opcoes, valor, onChange, testId,
}: {
  opcoes: { id: T; label: string; n: number }[];
  valor: T;
  onChange: (v: T) => void;
  testId?: string;
}) {
  return (
    // `scrollbar-none`: no Safari do celular a barra de rolagem aparecia POR CIMA
    // dos chips enquanto se arrastava. O gesto de arrastar já é o indicador — e o
    // último chip cortado na borda diz que há mais para o lado.
    <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1 -mx-1 px-1" data-testid={testId}>
      {opcoes.map(o => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          aria-pressed={valor === o.id}
          data-testid={`chip-situacao-${o.id}`}
          className={cn(
            "shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium hover-elevate",
            valor === o.id ? "border-primary bg-primary/10 text-primary" : "bg-card",
          )}
        >
          {o.label}
          <span className="tabular-nums text-[11px] text-muted-foreground">{o.n}</span>
        </button>
      ))}
    </div>
  );
}

/** Avatar com iniciais. Verde para quem está no Rizoma, neutro para registro próprio. */
export function AvatarProdutor({ nome, noRizoma, tamanho = "md" }: { nome: string; noRizoma: boolean; tamanho?: "sm" | "md" }) {
  return (
    <span
      aria-hidden
      className={cn(
        "rounded-full grid place-items-center font-semibold shrink-0",
        tamanho === "sm" ? "h-7 w-7 text-[10px]" : "h-9 w-9 text-xs",
        noRizoma ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground",
      )}
    >
      {iniciais(nome)}
    </span>
  );
}
